import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Euro, FileText, Car, AlertTriangle, Shield, Wrench, Tag, Plus, X } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const StatBox = ({ label, value, icon: Icon, color = "#ff0033", testid }) => (
  <div data-testid={testid} className="bg-white/[0.02] border border-white/10 rounded-2xl p-4">
    <div className="flex items-center gap-2 mb-2">
      {Icon && <Icon size={14} style={{ color }} />}
      <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
    </div>
    <div className="text-2xl font-black">{value}</div>
  </div>
);

export default function CustomerInsights() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [allTags, setAllTags] = useState([]);
  const [newTag, setNewTag] = useState("");

  const load = async () => {
    try {
      const [r, t] = await Promise.all([
        api.get(`/insights/customer/${id}`),
        api.get("/tags"),
      ]);
      setData(r.data);
      setAllTags(t.data?.items || []);
    } catch (e) { toast.error("Impossibile caricare insights"); }
  };

  useEffect(() => { load(); }, [id]);

  const createTag = async () => {
    if (!newTag.trim()) return;
    try {
      await api.post("/tags", { name: newTag.trim() });
      setNewTag(""); load();
      toast.success("Tag creato");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const toggleTag = async (tid) => {
    const currentIds = (data.tags || []).map(t => t.id);
    const newIds = currentIds.includes(tid) ? currentIds.filter(x => x !== tid) : [...currentIds, tid];
    try {
      await api.post(`/customers/${id}/tags`, { tag_ids: newIds });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  if (!data) return <div className="text-white/60">Caricamento...</div>;

  const c = data.customer;
  const k = data.kpi;
  const riskColor = {
    critical: "bg-rose-500/20 border-rose-500/40 text-rose-300",
    high: "bg-orange-500/20 border-orange-500/40 text-orange-300",
    medium: "bg-amber-500/20 border-amber-500/40 text-amber-300",
    low: "bg-emerald-500/20 border-emerald-500/40 text-emerald-300",
  }[k.risk_level] || "bg-white/10 border-white/20 text-white/60";

  return (
    <div className="space-y-6" data-testid="customer-insights">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="min-w-0 flex-1">
          <Link to="/admin/customers" className="text-xs text-white/40 inline-flex items-center gap-1 hover:text-white/80"><ArrowLeft size={12}/> Torna ai clienti</Link>
          <h1 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight mt-2 break-words">{c.nome} {c.cognome}</h1>
          <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-white/50 mt-1 flex-wrap">
            <span className="break-all">{c.email}</span>
            <span className="hidden sm:inline">·</span>
            <span>{c.telefono || "—"}</span>
            <span className="hidden sm:inline">·</span>
            <span>{c.nazione || "—"} {c.cap || ""}</span>
          </div>
        </div>
        {typeof k.risk_score === "number" && (
          <div className={`px-4 py-2 rounded-xl border ${riskColor}`} data-testid="insights-risk">
            <div className="text-[10px] uppercase tracking-widest">Risk Score</div>
            <div className="text-2xl font-black font-mono">{k.risk_score}/100</div>
            <div className="text-[10px] uppercase tracking-widest mt-0.5">{k.risk_level}</div>
          </div>
        )}
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatBox label="Spesa totale" value={`€${k.total_spent.toLocaleString()}`} icon={Euro} color="#22c55e" testid="kpi-spent" />
        <StatBox label="Contratti attivi" value={k.active_contracts} icon={FileText} color="#3b82f6" testid="kpi-active" />
        <StatBox label="Conclusi" value={k.completed_contracts} icon={Car} color="#22c55e" testid="kpi-done" />
        <StatBox label="Multe aperte" value={k.open_multe} icon={AlertTriangle} color="#facc15" testid="kpi-multe" />
        <StatBox label="Danni" value={k.damages_count} icon={Wrench} color="#fb923c" testid="kpi-damages" />
        <StatBox label="Compliance flags" value={k.compliance_flags} icon={Shield} color="#ef4444" testid="kpi-flags" />
      </div>

      {/* TAGS */}
      <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Tag size={14} className="text-[#ff0033]" />
          <h3 className="text-xs uppercase tracking-widest text-white/60">Tags</h3>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {data.tags.length === 0 && <span className="text-xs text-white/30">Nessun tag assegnato</span>}
          {data.tags.map((t) => (
            <button key={t.id} data-testid={`tag-${t.id}`} onClick={() => toggleTag(t.id)} className="px-3 py-1 rounded-full text-[11px] border inline-flex items-center gap-1" style={{ background: `${t.color}22`, borderColor: `${t.color}80`, color: t.color }}>
              {t.name} <X size={10} />
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-[10px] uppercase tracking-widest text-white/40">Aggiungi:</span>
          {allTags.filter(t => !data.tags.some(ct => ct.id === t.id)).map((t) => (
            <button key={t.id} onClick={() => toggleTag(t.id)} className="px-2 py-1 rounded text-[11px] bg-white/5 border border-white/10 hover:bg-white/10">+ {t.name}</button>
          ))}
          <input data-testid="new-tag" placeholder="nuovo tag..." value={newTag} onChange={(e) => setNewTag(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createTag()} className="bg-white/5 border border-white/10 rounded px-2 py-1 text-[11px] w-32" />
          <button data-testid="create-tag" onClick={createTag} className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-300 text-[11px] inline-flex items-center gap-1"><Plus size={11}/> crea</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: "overview", label: "Overview" },
          { id: "contracts", label: `Contratti (${data.contracts.length})` },
          { id: "multe", label: `Multe (${data.multe.length})` },
          { id: "events", label: `Compliance (${data.compliance_events.length})` },
        ].map((t) => (
          <button key={t.id} data-testid={`insights-tab-${t.id}`} onClick={() => setTab(t.id)} className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-[10px] sm:text-xs uppercase tracking-widest ${tab === t.id ? "bg-[#ff0033] text-white" : "bg-white/5 text-white/60 hover:bg-white/10"}`}>{t.label}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 text-sm space-y-2">
          <div><span className="text-white/40">CF:</span> <span className="font-mono">{c.codice_fiscale || "—"}</span></div>
          <div><span className="text-white/40">Documento:</span> <span className="font-mono">{c.patente_numero || c.ci_numero || "—"}</span></div>
          <div><span className="text-white/40">Indirizzo:</span> {c.indirizzo || "—"}</div>
          <div><span className="text-white/40">Lingua:</span> {c.lingua || "—"}</div>
          <div><span className="text-white/40">Blacklist locale:</span> {c.blacklist ? <span className="text-rose-300">SÌ ({c.blacklist_motivo || ""})</span> : <span className="text-emerald-300">No</span>}</div>
        </div>
      )}

      {tab === "contracts" && (
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-black/40 text-white/60 text-[10px] uppercase tracking-widest">
              <tr><th className="text-left p-3">N°</th><th className="text-left p-3">Periodo</th><th className="text-left p-3">Stato</th><th className="text-right p-3">Importo</th></tr>
            </thead>
            <tbody>
              {data.contracts.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-white/40 text-xs">Nessun contratto</td></tr>}
              {data.contracts.map((co) => (
                <tr key={co.id} className="border-t border-white/5">
                  <td className="p-3 font-mono text-xs">{co.numero}</td>
                  <td className="p-3 text-xs">{co.data_inizio?.slice(0,10)} → {co.data_fine?.slice(0,10)}</td>
                  <td className="p-3"><span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-white/5 border border-white/10">{co.stato}</span></td>
                  <td className="p-3 text-right font-mono">€{(co.costo_totale || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "multe" && (
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-black/40 text-white/60 text-[10px] uppercase tracking-widest">
              <tr><th className="text-left p-3">Data</th><th className="text-left p-3">Targa</th><th className="text-left p-3">Tipo</th><th className="text-right p-3">Importo</th></tr>
            </thead>
            <tbody>
              {data.multe.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-white/40 text-xs">Nessuna multa</td></tr>}
              {data.multe.map((m, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="p-3 text-xs">{m.data_verbale?.slice(0,10) || m.data_infrazione?.slice(0,10) || "—"}</td>
                  <td className="p-3 font-mono text-xs">{m.targa}</td>
                  <td className="p-3 text-xs">{m.tipo || m.descrizione || "—"}</td>
                  <td className="p-3 text-right font-mono">€{(m.importo || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "events" && (
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-black/40 text-white/60 text-[10px] uppercase tracking-widest">
              <tr><th className="text-left p-3">Quando</th><th className="text-left p-3">Evento</th><th className="text-left p-3">Dettagli</th></tr>
            </thead>
            <tbody>
              {data.compliance_events.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-white/40 text-xs">Nessun evento compliance</td></tr>}
              {data.compliance_events.map((e, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="p-3 text-[11px] text-white/60">{e.checked_at?.slice(0,19).replace("T"," ")}</td>
                  <td className="p-3"><span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-rose-500/15 border border-rose-500/40 text-rose-300">{e.kind}</span></td>
                  <td className="p-3 text-[11px] text-white/60">{e.risk_level || e.funding || e.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
